/**
 * Identity posture checks (C8) — ported from x365 modules/identity.js into pure,
 * deterministic functions that emit ControlResult[] for the evidence engine.
 *
 * Fetch (collectIdentityData) is separated from evaluation (evaluateIdentity) so the
 * scoring logic is unit-testable without Graph.
 */
import type { ControlResult, AffectedObject } from '@/compliance/evidence/types';
import {
  IDENTITY_ENDPOINTS,
  getConditionalAccessPolicies,
  getDirectoryRoleMembers,
  getMfaRegistrationReport,
  getPimAssignments,
  getSecurityDefaults,
  getUsers,
  type AuditUser,
  type CaPolicy,
  type DirectoryRoleWithMembers,
  type MfaRegistration,
  type PimAssignment,
} from '@/graph/modules/identity';

const STALE_DAYS = 90;
const GLOBAL_ADMIN_TMPL = '62e90394-69f5-4237-9190-012177145e10';
const HIGH_PRIV_ROLE_TEMPLATES = new Set<string>([
  '62e90394-69f5-4237-9190-012177145e10', // Global Administrator
  'e8611ab8-c189-46e8-94e1-60213ab1f814', // Privileged Role Administrator
  '7be44c8a-adaf-4e2a-84d6-ab2649e08a13', // Privileged Authentication Administrator
  '194ae4cb-b126-40b2-bd5b-6091b380977d', // Security Administrator
  'f28a1f50-f6e7-4571-818b-6a12f2af6b6c', // SharePoint Administrator
  'fe930be7-5e62-47db-91af-98c3a49a38b1', // User Account Administrator
  '9b895d92-2cd3-44c7-9d02-a6ac2d5ea5c3', // Application Administrator
  'cf1c38e5-3621-4004-a7cb-879624dced7c', // Application Developer
]);

export interface IdentityData {
  users: AuditUser[];
  mfaReport: MfaRegistration[];
  caPolicies: CaPolicy[];
  roles: DirectoryRoleWithMembers[];
  pim: PimAssignment[];
  securityDefaults: { isEnabled: boolean } | null;
}

/** Fetch everything the identity checks need. Best-effort per source. */
export async function collectIdentityData(): Promise<IdentityData> {
  const [users, mfaReport, caPolicies, roles, pim, securityDefaults] = await Promise.all([
    getUsers().catch(() => [] as AuditUser[]),
    getMfaRegistrationReport().catch(() => [] as MfaRegistration[]),
    getConditionalAccessPolicies().catch(() => [] as CaPolicy[]),
    getDirectoryRoleMembers().catch(() => [] as DirectoryRoleWithMembers[]),
    getPimAssignments().catch(() => [] as PimAssignment[]),
    getSecurityDefaults().catch(() => null),
  ]);
  return { users, mfaReport, caPolicies, roles, pim, securityDefaults };
}

const lower = (s?: string | null) => (s ?? '').toLowerCase();
const userObj = (u: { id: string; displayName?: string | null; userPrincipalName?: string | null }): AffectedObject => ({
  type: 'user',
  id: u.id,
  label: u.displayName ?? u.userPrincipalName ?? u.id,
  detail: u.userPrincipalName ?? undefined,
});

function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

/** Pure evaluation → one ControlResult per identity control tested. */
export function evaluateIdentity(data: IdentityData): ControlResult[] {
  const { users, mfaReport, caPolicies, roles, pim, securityDefaults } = data;
  const enabled = users.filter((u) => u.accountEnabled && u.userType !== 'Guest');
  const guests = users.filter((u) => u.userType === 'Guest');
  const results: ControlResult[] = [];

  // 1. Users with no MFA registered.
  const mfaMap = new Map(mfaReport.map((r) => [lower(r.userPrincipalName), r]));
  const noMfa = enabled.filter((u) => {
    const r = mfaMap.get(lower(u.userPrincipalName));
    if (!r) return true;
    return !r.isMfaRegistered && !r.isMfaCapable;
  });
  results.push({
    controlId: 'identity_user_no_mfa',
    status: noMfa.length > 0 ? 'fail' : 'pass',
    affected: noMfa.map(userObj),
    endpoints: [IDENTITY_ENDPOINTS.users, IDENTITY_ENDPOINTS.mfaReport],
  });

  // 2. Users not covered by any enabled CA policy.
  const enabledPolicies = caPolicies.filter((p) => p.state === 'enabled');
  const covered = new Set<string>();
  let coversAll = false;
  for (const p of enabledPolicies) {
    const incl = p.conditions?.users?.includeUsers ?? [];
    if (incl.includes('All') || incl.includes('GuestsOrExternalUsers')) {
      coversAll = true;
      break;
    }
    incl.forEach((id) => covered.add(id));
  }
  const outsideCa = coversAll ? [] : enabled.filter((u) => !covered.has(u.id));
  const caFails = outsideCa.length > 0 || enabledPolicies.length === 0;
  results.push({
    controlId: 'identity_user_no_ca_policy',
    status: caFails ? 'fail' : 'pass',
    affected:
      enabledPolicies.length === 0
        ? [{ type: 'tenant', id: 'ca', label: 'No enabled Conditional Access policy', detail: `${caPolicies.length} policies, 0 enabled` }]
        : outsideCa.map(userObj),
    endpoints: [IDENTITY_ENDPOINTS.caPolicies, IDENTITY_ENDPOINTS.users],
  });

  // 3. Privileged roles assigned permanently (no PIM).
  const eligible = new Set(pim.map((a) => `${a.roleDefinitionId}:${a.principalId}`));
  const permanent: AffectedObject[] = [];
  for (const role of roles) {
    if (!role.roleTemplateId || !HIGH_PRIV_ROLE_TEMPLATES.has(role.roleTemplateId)) continue;
    for (const m of role.members) {
      if (!eligible.has(`${role.roleTemplateId}:${m.id}`)) {
        permanent.push({
          type: 'role',
          id: `${role.id}:${m.id}`,
          label: m.displayName ?? m.userPrincipalName ?? m.id,
          detail: `permanent ${role.displayName ?? 'privileged role'}`,
        });
      }
    }
  }
  results.push({
    controlId: 'identity_admin_no_pim',
    status: permanent.length > 0 ? 'fail' : 'pass',
    affected: permanent,
    endpoints: [IDENTITY_ENDPOINTS.directoryRoles, IDENTITY_ENDPOINTS.pim],
  });

  // 4. Global Administrator sprawl (> 4 permanent).
  const gaRole = roles.find((r) => r.roleTemplateId === GLOBAL_ADMIN_TMPL);
  const gaMembers = gaRole?.members ?? [];
  results.push({
    controlId: 'identity_global_admin_sprawl',
    status: gaMembers.length > 4 ? 'fail' : 'pass',
    affected: gaMembers.map((m) => ({
      type: 'role' as const,
      id: m.id,
      label: m.displayName ?? m.userPrincipalName ?? m.id,
      detail: 'Global Administrator',
    })),
    endpoints: [IDENTITY_ENDPOINTS.directoryRoles],
  });

  // 5. Stale guest accounts (> 90 days).
  const staleGuests = guests.filter((u) => {
    const d = daysSince(u.signInActivity?.lastSignInDateTime);
    return d === null || d > STALE_DAYS;
  });
  results.push({
    controlId: 'identity_stale_guest',
    status: staleGuests.length > 0 ? 'fail' : 'pass',
    affected: staleGuests.map((u) => {
      const d = daysSince(u.signInActivity?.lastSignInDateTime);
      return { ...userObj(u), detail: d === null ? 'no sign-in recorded' : `${d} days since sign-in` };
    }),
    endpoints: [IDENTITY_ENDPOINTS.users],
  });

  // 6. Legacy authentication not blocked.
  const legacyBlocked = caPolicies.some((p) => {
    if (p.state !== 'enabled') return false;
    const apps = p.conditions?.clientAppTypes ?? [];
    const hasLegacy = apps.includes('exchangeActiveSync') || apps.includes('other');
    const blocks =
      p.grantControls?.operator === 'OR' && (p.grantControls?.builtInControls ?? []).includes('block');
    return hasLegacy && blocks;
  });
  results.push({
    controlId: 'identity_legacy_auth_allowed',
    status: legacyBlocked ? 'pass' : 'fail',
    affected: legacyBlocked
      ? []
      : [{ type: 'tenant', id: 'legacy', label: 'No CA policy blocks legacy authentication' }],
    endpoints: [IDENTITY_ENDPOINTS.caPolicies],
  });

  // 7. No baseline protection (Security Defaults off AND no enabled CA policy).
  const noBaseline = securityDefaults != null && !securityDefaults.isEnabled && enabledPolicies.length === 0;
  results.push({
    controlId: 'identity_no_security_baseline',
    status: noBaseline ? 'fail' : 'pass',
    affected: noBaseline
      ? [{ type: 'tenant', id: 'baseline', label: 'Security Defaults off and no enabled Conditional Access policy' }]
      : [],
    endpoints: [IDENTITY_ENDPOINTS.securityDefaults, IDENTITY_ENDPOINTS.caPolicies],
  });

  // 8. Stale enabled member accounts (> 90 days; only those with a recorded sign-in).
  const staleMembers = enabled.filter((u) => {
    const d = daysSince(u.signInActivity?.lastSignInDateTime);
    return d !== null && d > STALE_DAYS;
  });
  results.push({
    controlId: 'identity_stale_member',
    status: staleMembers.length > 0 ? 'fail' : 'pass',
    affected: staleMembers.map((u) => {
      const d = daysSince(u.signInActivity?.lastSignInDateTime);
      return { ...userObj(u), detail: `${d} days since sign-in` };
    }),
    endpoints: [IDENTITY_ENDPOINTS.users],
  });

  return results;
}

export async function auditIdentity(): Promise<ControlResult[]> {
  return evaluateIdentity(await collectIdentityData());
}
